import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../store/rootReducer';
import { closeSubscriptionModal } from '../../../store/subscription/subscriptionSlice';
import { GlobalDiscoveryService } from '../../../app/brand/global-discovery/service';
import { ErrorToast } from '../../../utils/toasters';
import { StripePackage, SubscriptionInterval } from '../../../model/shared/Subscription';
import ToggleSwitch from '../ToggleSwitch';
import CardLayout from '../card/CardLayout';
import { priceSplitter } from '../../../utils/helpers/priceSplitter';
import { SubscriptionStatusEnum } from '../../../enum/SubscriptionStatusEnum';
import { actions } from '../../../store/authentication/authRedux';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import DOMPurify from 'dompurify';
import { gtmService } from '../../../utils/gtm.service';

type IntervalData = {
    price: number;
    descriptionEn: string | null;
    descriptionDe: string | null;
    stripePriceId: string;
    savingPercentage?: number;
};

export interface StripePackageUI
    extends Omit<
        StripePackage,
        | 'monthlyPrice'
        | 'annualPrice'
        | 'monthlyDescEn'
        | 'monthlyDescDe'
        | 'annualDescEn'
        | 'annualDescDe'
        | 'stripeMonthlyPriceId'
        | 'stripeAnnualPriceId'
        | 'savingPercentage'
    > {
    interval: {
        monthly: IntervalData;
        annual: IntervalData;
    };
}
function UpgradeSubscriptionPlanModal() {
    const [packages, setPackages] = useState<StripePackageUI[]>([]);
    const [selectedPackages, setSelectedPackages] = useState<StripePackageUI>();
    const [subscriptionInterval, setSubscriptionInterval] = useState<SubscriptionInterval>('monthly');
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const { activeModal } = useSelector((state: IAllStates) => state.subscription);
    const language = useSelector((state: IAllStates) => state.auth.user.locale);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isUpgradeModalActive = activeModal === 'upgrade';
    const isAnnualActive = subscriptionInterval === 'annual';
    const isMonthlyActive = subscriptionInterval === 'monthly';
    const isGerman = language === 'de';
    const hasActiveSubscription = packages.some(
        p => p.isSubscribed && [SubscriptionStatusEnum.ACTIVE, SubscriptionStatusEnum.TRIALING].includes(p.status)
    );
    const hasInactiveSubscription = packages.some(p =>
        [SubscriptionStatusEnum.CANCELED, SubscriptionStatusEnum.PAST_DUE, SubscriptionStatusEnum.INCOMPLETE].includes(
            p.status
        )
    );

    const intervalTranslation =
        selectedPackages?.activeInterval === 'monthly' ? (isGerman ? 'Monat' : 'month') : isGerman ? 'Jahr' : 'year';
    const changeSwitchHandler = (isChecked: boolean) => {
        setSubscriptionInterval(isChecked ? 'annual' : 'monthly');
    };

    useEffect(() => {
        if (isUpgradeModalActive) {
            const getStripePackages = () => {
                GlobalDiscoveryService.getStripePackages()
                    .then(response => {
                        const mapped: StripePackageUI[] = response.data.map((item: StripePackage) => ({
                            ...item,
                            interval: {
                                monthly: {
                                    price: item.monthlyPrice,
                                    descriptionEn: item.monthlyDescEn,
                                    descriptionDe: item.monthlyDescDe,
                                    stripePriceId: item.stripeMonthlyPriceId,
                                },
                                annual: {
                                    price: item.annualPrice,
                                    descriptionEn: item.annualDescEn,
                                    descriptionDe: item.annualDescDe,
                                    stripePriceId: item.stripeAnnualPriceId,
                                    savingPercentage: item?.savingPercentage,
                                },
                            },
                        }));
                        setPackages(mapped);
                        const activeInterval = getActiveInterval(mapped);

                        if (activeInterval) {
                            setSubscriptionInterval(activeInterval);
                        }
                    })
                    .catch(error => ErrorToast(error));
            };

            getStripePackages();
        }
    }, [isUpgradeModalActive]);

    const startTrialAction = async (priceId: string, packageId: number) => {
        try {
            setLoadingId(packageId);

            const response = await GlobalDiscoveryService.stripeCreateSession(priceId);

            window.location.href = response.url;
        } catch (err) {
            await ErrorToast(err);
        }
    };
    const cancelSubscriptionAction = async (packageId: number) => {
        try {
            setLoadingId(packageId);
            const response = await GlobalDiscoveryService.cancelSubscription();
            window.location.href = response.url;
        } catch (err) {
            await ErrorToast(err);
        }
    };
    const switchToPaidApiRequest = async (packageId: number) => {
        try {
            setLoadingId(packageId);
            const response = await GlobalDiscoveryService.switchToPaidSubscription();
            dispatch(actions.updateSubscriptionPlan(response));
            await gtmService('discovery_subscribed');
            closeModalAction();
        } catch (err) {
            await ErrorToast(err);
        } finally {
            setLoadingId(null);
        }
    };

    const switchToPaidAction = (selectedPackage: StripePackageUI) => {
        setSelectedPackages(selectedPackage);
        setShowConfirmationModal(true);
    };
    const closeModalAction = () => {
        dispatch(closeSubscriptionModal());
    };

    const getActiveInterval = (packages: StripePackageUI[]): SubscriptionInterval | null => {
        const activePackage = packages.find(
            p => p.isSubscribed && [SubscriptionStatusEnum.ACTIVE, SubscriptionStatusEnum.TRIALING].includes(p.status)
        );

        return activePackage?.activeInterval ?? null;
    };

    return (
        <>
            <Modal
                onHide={closeModalAction}
                show={isUpgradeModalActive}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered={true}
            >
                <Modal.Body className="text-center mb-4">
                    <h5 className="my-3">{t(`general.stripe.upgrade_modal_title`)}</h5>
                    <div className="d-flex align-items-center justify-content-center">
                        <h5 className={`mb-0 pr-3 ${isMonthlyActive ? 'text-primary' : 'text-muted'}`}>
                            {t(`general.stripe.monthly`)}
                        </h5>
                        <ToggleSwitch
                            onChange={(checked: boolean) => changeSwitchHandler(checked)}
                            checked={subscriptionInterval === 'annual'}
                            id="subscriptionInterval"
                        />
                        <h5 className={`mb-0 pl-3 ${isAnnualActive ? 'text-primary' : 'text-muted'}`}>
                            {t(`general.stripe.yearly`)}
                        </h5>
                    </div>
                    <Row className="mt-3 px-lg-5 px-2">
                        {packages.map(item => {
                            const currentInterval = item.interval[subscriptionInterval];
                            const isCurrentInterval = item.activeInterval === subscriptionInterval;
                            const status = item.status;
                            const isSubscribed = item.isSubscribed && isCurrentInterval;
                            const description = isGerman
                                ? currentInterval?.descriptionDe
                                : currentInterval?.descriptionEn;
                            const canUpgrade = hasActiveSubscription;

                            const isTrial = status === SubscriptionStatusEnum.TRIALING && isCurrentInterval;

                            const isCanceled = status === SubscriptionStatusEnum.CANCELED && isCurrentInterval;

                            const isActive = status === SubscriptionStatusEnum.ACTIVE && isCurrentInterval;

                            const handleAction = () => {
                                if (isActive) return;

                                if (isTrial) {
                                    return switchToPaidAction(item);
                                }
                                if (canUpgrade) {
                                    return (window.location.href = String(process.env.VITE_REFILL_TOKEN_CALENDLY));
                                }

                                return startTrialAction(currentInterval.stripePriceId, item.id);
                            };

                            return (
                                <Col className="mb-4 mb-lg-0 d-flex" lg={6} key={item.id}>
                                    <CardLayout
                                        classBody="d-flex flex-column p-0"
                                        style={{ borderColor: isSubscribed ? item?.color : '', borderWidth: '2px' }}
                                    >
                                        <img
                                            height="160"
                                            className="object-fit-cover default-radius-sm-top"
                                            src={item?.image?.path ?? ''}
                                            alt={item?.image?.name}
                                        />
                                        <div className="p-3 d-flex flex-column h-100">
                                            <h4 className="mb-3">
                                                {t(`general.stripe.number_tokens`, {
                                                    numberTokens: item?.discoveryTokens ?? '-',
                                                })}
                                            </h4>
                                            <div
                                                className="text-start"
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(description ?? '-'),
                                                }}
                                            />
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center justify-content-center mb-1">
                                                    <p
                                                        style={{ color: item.color }}
                                                        className="font-18 font-weight-bold euro-symbol mb-0 "
                                                    >
                                                        {priceSplitter(currentInterval.price)}
                                                    </p>
                                                    {currentInterval?.savingPercentage && (
                                                        <div className="bg-success text-white px-2 py-1 badge-pill ml-3 font-12">
                                                            {t(`general.stripe.spare_percentage`, {
                                                                discount: currentInterval.savingPercentage ?? 0,
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-muted">
                                                    {isMonthlyActive
                                                        ? t(`general.stripe.pay_monthly`)
                                                        : t(`general.stripe.pay_yearly`)}
                                                </p>
                                                <Button
                                                    variant={isActive ? 'secondary' : 'primary'}
                                                    style={
                                                        !isSubscribed
                                                            ? { borderColor: item.color, backgroundColor: item.color }
                                                            : {}
                                                    }
                                                    className="w-100"
                                                    onClick={handleAction}
                                                    disabled={loadingId === item.id || isActive}
                                                >
                                                    {isActive
                                                        ? t(`general.stripe.current_subscription`)
                                                        : isTrial
                                                        ? t(`general.stripe.switch_to_paid`)
                                                        : canUpgrade
                                                        ? t(`general.stripe.upgrade_now`)
                                                        : hasInactiveSubscription
                                                        ? t(`general.stripe.subscribe`)
                                                        : t(`general.stripe.start_trial`)}
                                                </Button>{' '}
                                                {isSubscribed && (
                                                    <p
                                                        onClick={
                                                            loadingId === item.id
                                                                ? undefined
                                                                : () => cancelSubscriptionAction(item.id)
                                                        }
                                                        className={`mt-3 text-muted ${
                                                            loadingId === item.id
                                                                ? 'cursor-not-allowed opacity-50'
                                                                : 'cursor-pointer'
                                                        }`}
                                                    >
                                                        {t(`general.stripe.cancel_anytime`)}
                                                    </p>
                                                )}
                                                {canUpgrade && !isSubscribed && (
                                                    <p
                                                        onClick={() =>
                                                            (window.location.href = String(
                                                                process.env.VITE_REFILL_TOKEN_CALENDLY
                                                            ))
                                                        }
                                                        className="text-muted mt-3 cursor-pointer"
                                                    >
                                                        {t(`general.stripe.talk_to_us`)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardLayout>
                                </Col>
                            );
                        })}{' '}
                    </Row>
                </Modal.Body>
            </Modal>
            <ConfirmationModal
                title={t('general.stripe.confirm_package_title', { packageName: selectedPackages?.displayName })}
                description={t('general.stripe.confirm_package_desc', {
                    packageName: selectedPackages?.displayName,
                    price: selectedPackages?.interval[subscriptionInterval].price,
                    interval: intervalTranslation,
                })}
                iconUrl="/icons/packages/img_package_overview.png"
                show={showConfirmationModal}
                closeModal={() => setShowConfirmationModal(false)}
                action={switchToPaidApiRequest}
            />
        </>
    );
}
export default UpgradeSubscriptionPlanModal;
